import React, { useRef } from 'react'

import { assertSupportedHideConfig } from '@ui-kit/components/formComponents/DateTimePicker/SegmentedDateTimePicker/helpers'
import { useSegmentedDateInput } from '@ui-kit/components/formComponents/DateTimePicker/SegmentedDateTimePicker/useSegmentedDateInput'
import type { SegmentedVariantProps } from '@ui-kit/components/formComponents/DateTimePicker/types'
import TextField from '@ui-kit/components/formComponents/TextField/index.web'
import Columns from '@ui-kit/components/layoutComponents/Columns'
import Stack from '@ui-kit/components/layoutComponents/Stack'
import { FORM_ELEMENT } from '@ui-kit/constants'
import { caption } from '@ui-kit/core/typography'

export const SegmentedDateTimePicker = (props: SegmentedVariantProps) => {
  const hideDay = props.hideDay ?? false
  const hideMonth = props.hideMonth ?? false
  const hideYear = props.hideYear ?? false

  const monthRef = useRef<HTMLInputElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)

  const {
    day,
    month,
    year,
    fieldToAutoFocus,
    isComplete,
    hasBlurred,
    onDayChange,
    onMonthChange,
    onYearChange,
    onDayBlur,
    onMonthBlur,
    onYearBlur,
    onSegmentFocus,
  } = useSegmentedDateInput(
    {
      value: props.value,
      onValueChange: props.onValueChange,
      hideDay,
      hideMonth,
      hideYear,
    },
    { monthRef, yearRef }
  )

  assertSupportedHideConfig(props)

  const autoFocus = props.autoFocus ?? false
  const styleType = props.styleType ?? 'underlined'

  const shouldShowError = Boolean(props.errorMessage) && (isComplete || hasBlurred)
  const status = shouldShowError ? 'error' : undefined

  return (
    <Stack space="xxsmall">
      {props.label ? <span className={caption({ color: 'grey700' })}>{props.label}</span> : null}
      <Columns space="medium">
        {!hideDay && (
          <TextField
            id={`${props.name}-day`}
            styleType={styleType}
            name={`${props.name}-day`}
            disabled={props.disabled}
            label={props.placeholders.day}
            value={day}
            type="tel"
            onFocus={onSegmentFocus}
            onBlur={onDayBlur}
            onValueChange={onDayChange}
            status={status}
            autoFocus={autoFocus && fieldToAutoFocus === 'day'}
          />
        )}

        {!hideMonth && (
          <TextField
            styleType={styleType}
            id={`${props.name}-month`}
            name={`${props.name}-month`}
            disabled={props.disabled}
            label={props.placeholders.month}
            value={month}
            type="tel"
            fieldRef={monthRef}
            onFocus={onSegmentFocus}
            onBlur={onMonthBlur}
            onValueChange={onMonthChange}
            status={status}
            autoFocus={autoFocus && fieldToAutoFocus === 'month'}
          />
        )}

        {!hideYear && (
          <TextField
            styleType={styleType}
            id={`${props.name}-year`}
            name={`${props.name}-year`}
            disabled={props.disabled}
            label={props.placeholders.year}
            value={year}
            type="tel"
            fieldRef={yearRef}
            onFocus={onSegmentFocus}
            onBlur={onYearBlur}
            onValueChange={onYearChange}
            status={status}
            autoFocus={autoFocus && fieldToAutoFocus === 'year'}
          />
        )}
      </Columns>
      {shouldShowError ? (
        <p className={caption({ color: FORM_ELEMENT.errorColorName })}>{props.errorMessage}</p>
      ) : null}
    </Stack>
  )
}
